/**
 * CommandPalette — global ⌘K / Ctrl+K launcher across the 62 destinations
 * of AgenteLead Pro. Uses the `cmdk` library for fuzzy search + keyboard
 * navigation. Mounted at the App root so any page can open it.
 *
 * Sources:
 *   • Pages — every navigable route (Dashboard, Funnel, B2C Radar, etc.)
 *   • Master Console — admin-only routes (filtered when user is not admin)
 *   • Actions — quick triggers (e.g. "Run SAM.gov sync now")
 *   • AI Command — when the user types a natural-language phrase (≥3 words
 *     OR ends with a question mark), an "🤖 Run AI: …" item appears at the
 *     top. Pressing Enter sends the prompt to /api/ai/command/parse and
 *     opens a preview card with the parsed action + 1-click confirm.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import {
  Search, LayoutDashboard, KanbanSquare, TrendingUp, Radar, Landmark,
  Instagram, Linkedin, Youtube, Inbox, Send, Plug, Library, Workflow,
  CreditCard, Calculator, Settings, FileText, Activity, Crown, Users,
  Package, BarChart3, Mail, ShieldCheck, Bot, Banknote, ArrowLeftRight,
  Sparkles, KeyRound, ClipboardCheck, Loader2, Wand2, ArrowRight, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
  { id: "dashboard",         label: "Dashboard",            icon: LayoutDashboard, route: "/dashboard",         group: "Pages" },
  { id: "kanban",            label: "CRM Kanban",           icon: KanbanSquare,    route: "/kanban",            group: "Pages" },
  { id: "funnel",            label: "Sales Funnel",         icon: TrendingUp,      route: "/funnel",            group: "Pages" },
  { id: "public-works",      label: "Procurement Scanner",  icon: Landmark,        route: "/public-works",      group: "Pages" },
  { id: "gov-kanban",        label: "Gov · Public Works",   icon: ClipboardCheck,  route: "/gov-kanban",        group: "Pages" },
  { id: "b2c-intent",        label: "B2C Intent · Listening", icon: Radar,         route: "/b2c-intent",        group: "Pages" },
  { id: "b2c-radar",         label: "B2C Lead Radar",       icon: Radar,           route: "/b2c-radar",         group: "Pages" },
  { id: "instagram",         label: "Instagram Scraper",    icon: Instagram,       route: "/instagram",         group: "Social" },
  { id: "linkedin",          label: "LinkedIn Sales Nav",   icon: Linkedin,        route: "/linkedin",          group: "Social" },
  { id: "youtube",           label: "YouTube Scraper",      icon: Youtube,         route: "/youtube",           group: "Social" },
  { id: "replies",           label: "AI Replies Inbox",     icon: Inbox,           route: "/replies",           group: "Outreach" },
  { id: "campaigns",         label: "Campaigns",            icon: Send,            route: "/campaigns",         group: "Outreach" },
  { id: "integrations",      label: "Integrations",         icon: Plug,            route: "/integrations",      group: "Setup" },
  { id: "webhooks",          label: "Webhooks",             icon: Plug,            route: "/webhooks",          group: "Setup" },
  { id: "library",           label: "Sequence Library",     icon: Library,         route: "/library",           group: "Outreach" },
  { id: "free-sources",      label: "Data Sources",         icon: FileText,        route: "/settings/sources",  group: "Setup" },
  { id: "marketing-apis",    label: "Marketing APIs",       icon: Plug,            route: "/marketing-apis",    group: "Setup" },
  { id: "marketplace",       label: "Marketplace",          icon: Package,         route: "/marketplace",       group: "Setup" },
  { id: "insights",          label: "Insights",             icon: BarChart3,       route: "/insights",          group: "Analytics" },
  { id: "log",               label: "Activity Log",         icon: FileText,        route: "/log",               group: "Analytics" },
  { id: "billing",           label: "Billing",              icon: CreditCard,      route: "/billing",           group: "Account" },
  { id: "roi",               label: "ROI Calculator",       icon: Calculator,      route: "/roi",               group: "Analytics" },
  { id: "config",            label: "Settings",             icon: Settings,        route: "/config",            group: "Account" },
  { id: "account",           label: "Account",              icon: Users,           route: "/account",           group: "Account" },
  { id: "subscription",      label: "Subscription",         icon: Banknote,        route: "/subscription",      group: "Account" },
  { id: "prospecting",       label: "Prospecting",          icon: Workflow,        route: "/prospecting",       group: "Pages" },
];

const MASTER_PAGES = [
  { id: "master",                    label: "Master Dashboard",     icon: Crown,         route: "/master",                     group: "Master" },
  { id: "master-tenants",            label: "Tenants",              icon: Users,         route: "/master/tenants",             group: "Master" },
  { id: "master-demos",              label: "Demo Profiles",        icon: ArrowLeftRight, route: "/master/demo-accounts",      group: "Master" },
  { id: "master-packages",           label: "Packages",             icon: Package,       route: "/master/packages",            group: "Master" },
  { id: "master-agents",             label: "Agents",               icon: Bot,           route: "/master/agents",              group: "Master" },
  { id: "master-analytics",          label: "A/B Analytics",        icon: BarChart3,     route: "/master/analytics",           group: "Master" },
  { id: "master-email-analytics",    label: "Email Analytics",      icon: Send,          route: "/master/email-analytics",     group: "Master" },
  { id: "master-procurement-pulse",  label: "Procurement Pulse",    icon: Landmark,      route: "/master/procurement-pulse",   group: "Master" },
  { id: "master-content",            label: "Content AI",           icon: Sparkles,      route: "/master/content",             group: "Master" },
  { id: "master-gmail",              label: "Gmail SMTP",           icon: Mail,          route: "/master/gmail",               group: "Master" },
  { id: "master-access-logs",        label: "Access Logs",          icon: ShieldCheck,   route: "/master/access-logs",         group: "Master" },
];

const ACTIONS = [
  { id: "act-run-samgov",  label: "Run SAM.gov sync now",      icon: Activity, route: "/public-works?tab=sources#samgov-cron-runnow", group: "Actions" },
  { id: "act-funnel",      label: "Open Sales Funnel",         icon: TrendingUp, route: "/funnel",                                     group: "Actions" },
  { id: "act-b2c-radar",   label: "Open B2C Lead Radar",       icon: Radar,    route: "/b2c-radar?tab=overview",                       group: "Actions" },
  { id: "act-impersonate", label: "Switch persona…",           icon: KeyRound, route: "/master/demo-accounts",                         group: "Actions" },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // AI command state — null = no preview, otherwise the parsed action card.
  const [aiPreview, setAiPreview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const role = (user || {}).role || "";
  const isAdmin = role === "super_admin" || role === "admin" || role === "master_admin";

  const items = isAdmin ? [...PAGES, ...MASTER_PAGES, ...ACTIONS] : [...PAGES, ...ACTIONS];

  // Toggle with ⌘K / Ctrl+K, close with Escape (cmdk handles Escape internally).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Heuristic: 3+ words OR ends with '?' → treat as a natural-language
  // command and surface the AI runner. Avoids triggering on short queries
  // like "kanban" or "funnel".
  const isNL = (s) => {
    const t = (s || "").trim();
    if (!t) return false;
    if (t.endsWith("?")) return true;
    return t.split(/\s+/).filter(Boolean).length >= 3;
  };
  const showAiOption = isNL(q) && !aiPreview;

  const runAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/command/parse`,
        { prompt: q.trim() });
      setAiPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "AI parse failed");
    } finally { setAiLoading(false); }
  };

  const executeAi = (preview) => {
    setOpen(false);
    const p = preview.params || {};
    switch (preview.action) {
      case "search_places": {
        // Hand off to the existing /prospecting cockpit which already
        // accepts ?business_type=&location=&auto=1 URL params and fires
        // the Google Maps search on mount. No new backend surface needed.
        const usp = new URLSearchParams();
        if (p.sector) usp.set("business_type", p.sector);
        if (p.location) usp.set("location", p.location);
        usp.set("auto", "1");
        navigate(`/prospecting?${usp.toString()}`);
        toast.success(preview.label || "Running search…");
        break;
      }
      case "search_b2c": {
        const usp = new URLSearchParams();
        if (p.keyword) usp.set("q", p.keyword);
        if (p.source) usp.set("source", p.source);
        if (p.intent_min != null) usp.set("intent_min", String(p.intent_min));
        navigate(`/b2c-radar?tab=leads&${usp.toString()}`);
        toast.success(preview.label || "Opening B2C radar…");
        break;
      }
      case "search_procurement": {
        const usp = new URLSearchParams();
        usp.set("tab", "search");
        if (p.state) usp.set("state", p.state);
        if (p.naics) usp.set("naics", p.naics);
        if (p.trade) usp.set("trade", p.trade);
        navigate(`/public-works?${usp.toString()}`);
        toast.success(preview.label || "Opening Procurement Scanner…");
        break;
      }
      case "navigate": {
        if (p.route && p.route.startsWith("/")) {
          navigate(p.route);
          toast.success(preview.label || "Navigating…");
        } else {
          toast.error("Invalid route");
        }
        break;
      }
      default:
        toast.error(p.reason || "Couldn't understand that command");
    }
    setAiPreview(null);
    setQ("");
  };

  const onSelect = useCallback((route) => {
    setOpen(false);
    setQ("");
    setAiPreview(null);
    if (route.startsWith("/")) navigate(route);
    else window.location.href = route;
  }, [navigate]);

  // Reset preview when dialog closes.
  useEffect(() => { if (!open) { setAiPreview(null); setAiLoading(false); } }, [open]);

  // Hide when not authenticated — palette is a power-user tool, no value
  // on the login page.
  if (!user) return null;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          data-testid="cmdk-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        />
      )}
      <Command.Dialog
        open={open}
        onOpenChange={setOpen}
        label="Command Palette"
        aria-describedby="cmdk-help"
        className="fixed top-[18%] left-1/2 -translate-x-1/2 w-[640px] max-w-[92vw] rounded-2xl border z-[9999] overflow-hidden shadow-2xl"
        style={{
          borderColor: "rgba(201,162,78,0.3)",
          background: "var(--al-card-bg, #1A0E36)",
          color: "var(--al-text, white)",
        }}
        data-testid="cmdk-dialog"
      >
        {/* SR-only title + description — silences Radix a11y warnings while
            keeping the visual design clean. */}
        <span id="cmdk-help" className="sr-only">
          Type to fuzzy-search across pages and actions. Use arrow keys to
          navigate and Enter to open.
        </span>
        <div className="flex items-center gap-2 px-4 border-b" style={{ borderColor: "var(--al-border)" }}>
          <Search className="w-4 h-4 opacity-60" />
          <Command.Input
            value={q}
            onValueChange={setQ}
            placeholder="Type to navigate · ⌘K to close"
            className="flex-1 h-12 bg-transparent outline-none text-[14px]"
            data-testid="cmdk-input"
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border opacity-60"
               style={{ borderColor: "var(--al-border)" }}>ESC</kbd>
        </div>

        <Command.List className="max-h-[420px] overflow-y-auto p-2">
          {/* AI Command — banner shown when the input looks like NL.
              Pressing the row (or Enter when it's the only match) triggers
              the parse → preview flow. */}
          {showAiOption && (
            <Command.Group heading="AI" forceMount
              className="text-[10px] uppercase tracking-wider opacity-60 px-2 pt-2">
              <Command.Item
                value={`__ai__ ${q}`}
                onSelect={runAi}
                forceMount
                data-testid="cmdk-ai-run"
                className="flex items-center gap-3 px-2 py-2.5 rounded-lg cursor-pointer text-[13px] aria-selected:bg-fuchsia-500/20 hover:bg-fuchsia-500/10 transition-colors border border-fuchsia-400/30 mb-2"
                style={{ background: "linear-gradient(90deg, rgba(168,85,247,0.10) 0%, rgba(217,70,239,0.05) 100%)" }}>
                {aiLoading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin text-fuchsia-300" />
                  : <Wand2 className="w-3.5 h-3.5 text-fuchsia-300" />}
                <span className="flex-1">
                  <span className="text-fuchsia-200 font-bold">Run AI:</span>{" "}
                  <span className="text-white">{q}</span>
                </span>
                <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded border border-fuchsia-400/40 text-fuchsia-200">
                  ⏎ GPT-4o
                </span>
              </Command.Item>
            </Command.Group>
          )}

          {/* AI Preview — replaces the list entirely while we have a parsed
              action waiting for confirmation. */}
          {aiPreview ? (
            <div data-testid="cmdk-ai-preview" className="px-2 py-2 space-y-3">
              <div className="rounded-xl border border-fuchsia-400/40 p-3.5"
                style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.12) 0%, rgba(15,8,32,0.6) 100%)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 className="w-3.5 h-3.5 text-fuchsia-300" />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-fuchsia-200">AI parsed</span>
                  <button onClick={() => setAiPreview(null)} data-testid="cmdk-ai-dismiss"
                    className="ml-auto text-white/50 hover:text-white" aria-label="Dismiss">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="text-[13.5px] font-bold text-white mb-1.5">
                  {aiPreview.label || aiPreview.action}
                </div>
                <div className="text-[10.5px] font-mono text-white/60 mb-3 break-words">
                  action: <span className="text-amber-300">{aiPreview.action}</span>
                  {Object.keys(aiPreview.params || {}).length > 0 && (
                    <span> · params: {JSON.stringify(aiPreview.params)}</span>
                  )}
                </div>
                {aiPreview.action === "unknown" ? (
                  <div className="text-[12px] text-rose-300 mb-2.5">
                    {aiPreview.params?.reason || "Couldn't map your request."}
                  </div>
                ) : (
                  <button onClick={() => executeAi(aiPreview)}
                    data-testid="cmdk-ai-confirm"
                    className="w-full h-9 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:from-fuchsia-400 hover:to-purple-400 text-white font-bold text-[12.5px] inline-flex items-center justify-center gap-1.5 transition-all">
                    <ArrowRight className="w-3.5 h-3.5" /> Run
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <Command.Empty className="py-8 text-center text-[12px] opacity-60">
                {q.trim() ? "No results — try a longer phrase to use AI." : "No results."}
              </Command.Empty>
              {["Pages", "Outreach", "Social", "Setup", "Analytics", "Account", "Master", "Actions"].map((group) => {
                const groupItems = items.filter((i) => i.group === group);
                if (groupItems.length === 0) return null;
                return (
                  <Command.Group key={group} heading={group}
                    className="text-[10px] uppercase tracking-wider opacity-60 px-2 pt-2">
                    {groupItems.map((it) => (
                      <Command.Item
                        key={it.id}
                        value={`${it.label} ${it.group}`}
                        onSelect={() => onSelect(it.route)}
                        data-testid={`cmdk-item-${it.id}`}
                        className="flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer text-[13px] aria-selected:bg-[#C9A24E]/20 aria-selected:text-white hover:bg-white/5 transition-colors"
                      >
                        <it.icon className="w-3.5 h-3.5 opacity-70" />
                        <span className="flex-1">{it.label}</span>
                        <span className="text-[10px] font-mono opacity-50">{it.route}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                );
              })}
            </>
          )}
        </Command.List>

        <div className="px-4 py-2 border-t text-[10.5px] opacity-60 flex items-center justify-between"
             style={{ borderColor: "var(--al-border)" }}>
          <span>↑↓ navigate · ⏎ select · esc close</span>
          <span>⌘K anywhere</span>
        </div>
      </Command.Dialog>
    </>
  );
}
